/**
 * Regras de janela de caixa alinhadas ao nó "Dia atual" do n8n (±2h do expediente, timezone America/Sao_Paulo).
 * Lógica pura — sem I/O. Persistência fica em api.ts (manage-caixa + update-order).
 */

/** Subconjunto de campos usados nas regras (evita dependência circular com api.ts). */
export type CaixaLike = {
  status: string;
  data_fechamento: string;
  valor_fechamento?: number;
};

export type HorarioFuncionamentoRow = {
  dia: string;
  status?: string;
  horario_inicio?: string;
  horario_fim?: string;
};

export type JanelaCaixaAtiva = {
  h: HorarioFuncionamentoRow;
  abertura: Date;
  fechamento: Date;
  aberturaCaixa: Date;
  fechamentoCaixa: Date;
  data_abertura_iso: string;
  data_fechamento_iso: string;
  agora_ts: number;
};

const DIAS_SEMANA = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];

function horarioRowOk(h: HorarioFuncionamentoRow): boolean {
  return !!(
    h &&
    h.status === "aberto" &&
    h.horario_inicio &&
    h.horario_fim
  );
}

/** Parse do mesmo formato armazenado em clientes.horario_funcionamento */
export function parseHorarioFuncionamentoRaw(raw: unknown): HorarioFuncionamentoRow[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw as HorarioFuncionamentoRow[];
  if (typeof raw === "string") {
    try {
      const p = JSON.parse(raw);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  if (typeof raw === "object" && raw !== null && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    if (Array.isArray(o.horarios)) return o.horarios as HorarioFuncionamentoRow[];
  }
  return [];
}

function montarData(base: Date, horaStr: string): Date {
  const [hh, mm] = horaStr.split(":").map(Number);
  const d = new Date(base);
  d.setHours(hh || 0, mm || 0, 0, 0);
  return d;
}

function formatarISOlocal(data: Date): string {
  const ano = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, "0");
  const dia = String(data.getDate()).padStart(2, "0");
  const hh = String(data.getHours()).padStart(2, "0");
  const mm = String(data.getMinutes()).padStart(2, "0");
  const ss = String(data.getSeconds()).padStart(2, "0");
  return `${ano}-${mes}-${dia}T${hh}:${mm}:${ss}`;
}

/**
 * "Agora" em America/Sao_Paulo (mesma abordagem do n8n: locale string → Date).
 */
export function agoraEmSaoPaulo(): Date {
  return new Date(new Date().toLocaleString("en-US", { timeZone: "America/Sao_Paulo" }));
}

/**
 * Acha o turno cuja janela [abertura-2h, fechamento+2h] contém `agora`.
 */
export function encontrarJanelaCaixaAtiva(
  horarios: HorarioFuncionamentoRow[],
  agora: Date = agoraEmSaoPaulo()
): JanelaCaixaAtiva | null {
  const diasSemana = DIAS_SEMANA;
  const idxHoje = agora.getDay();
  const diaCalendario = diasSemana[idxHoje];
  let melhor: JanelaCaixaAtiva | null = null;
  let melhorAberturaCaixa = -Infinity;

  for (let k = 0; k <= 7; k++) {
    const base = new Date(agora);
    base.setHours(0, 0, 0, 0);
    base.setDate(base.getDate() - k);
    const dowBase = base.getDay();

    for (const h of horarios) {
      if (!horarioRowOk(h)) continue;
      const idxAgenda = diasSemana.indexOf(h.dia);
      if (idxAgenda < 0 || idxAgenda !== dowBase) continue;

      let abertura = montarData(base, h.horario_inicio!);
      let fechamento = montarData(base, h.horario_fim!);
      if (fechamento <= abertura) {
        fechamento.setDate(fechamento.getDate() + 1);
      }

      const aberturaCaixa = new Date(abertura.getTime() - 2 * 60 * 60 * 1000);
      const fechamentoCaixa = new Date(fechamento.getTime() + 2 * 60 * 60 * 1000);
      const t = agora.getTime();

      if (t >= aberturaCaixa.getTime() && t <= fechamentoCaixa.getTime()) {
        if (aberturaCaixa.getTime() > melhorAberturaCaixa) {
          melhorAberturaCaixa = aberturaCaixa.getTime();
          melhor = {
            h,
            abertura,
            fechamento,
            aberturaCaixa,
            fechamentoCaixa,
            data_abertura_iso: formatarISOlocal(aberturaCaixa),
            data_fechamento_iso: formatarISOlocal(fechamentoCaixa),
            agora_ts: t,
          };
        }
      }
    }
  }

  return melhor;
}

/** Fallback quando nenhuma janela contém `agora` (próximo dia útil aberto). */
export function janelaCaixaFallback(
  horarios: HorarioFuncionamentoRow[],
  agora: Date = agoraEmSaoPaulo()
): JanelaCaixaAtiva {
  const diasSemana = DIAS_SEMANA;
  const idxHoje = agora.getDay();
  const diaCalendario = diasSemana[idxHoje];

  let resultado: HorarioFuncionamentoRow | null = horarios.find(
    (x) => x.dia === diaCalendario && horarioRowOk(x)
  ) ?? null;

  if (!resultado) {
    const candidatos = horarios.filter(horarioRowOk);
    if (candidatos.length === 0) {
      const base = new Date(agora);
      const aberturaCaixa = new Date(base);
      aberturaCaixa.setHours(0, 0, 0, 0);
      const fechamentoCaixa = new Date(aberturaCaixa);
      fechamentoCaixa.setHours(23, 59, 59, 999);
      return {
        h: { dia: diaCalendario, status: "aberto", horario_inicio: "00:00", horario_fim: "23:59" },
        abertura: aberturaCaixa,
        fechamento: fechamentoCaixa,
        aberturaCaixa,
        fechamentoCaixa,
        data_abertura_iso: formatarISOlocal(aberturaCaixa),
        data_fechamento_iso: formatarISOlocal(fechamentoCaixa),
        agora_ts: agora.getTime(),
      };
    }

    const idx = (nome: string) => diasSemana.indexOf(nome);
    const dist = (a: number, b: number) => {
      const d = Math.abs(a - b);
      return Math.min(d, 7 - d);
    };
    let best = candidatos[0];
    let bestDist = 99;
    let bestTras = 99;
    for (const h of candidatos) {
      const i = idx(h.dia);
      if (i < 0) continue;
      const d = dist(idxHoje, i);
      const tras = (idxHoje - i + 7) % 7;
      if (d < bestDist || (d === bestDist && tras < bestTras)) {
        bestDist = d;
        bestTras = tras;
        best = h;
      }
    }
    resultado = best;
  }

  let abertura = montarData(agora, resultado!.horario_inicio!);
  let fechamento = montarData(agora, resultado!.horario_fim!);
  if (fechamento <= abertura) {
    fechamento.setDate(fechamento.getDate() + 1);
  }
  const aberturaCaixa = new Date(abertura.getTime() - 2 * 60 * 60 * 1000);
  const fechamentoCaixa = new Date(fechamento.getTime() + 2 * 60 * 60 * 1000);

  return {
    h: resultado!,
    abertura,
    fechamento,
    aberturaCaixa,
    fechamentoCaixa,
    data_abertura_iso: formatarISOlocal(aberturaCaixa),
    data_fechamento_iso: formatarISOlocal(fechamentoCaixa),
    agora_ts: agora.getTime(),
  };
}

export function getJanelaParaNovoCaixa(
  horarios: HorarioFuncionamentoRow[],
  agora: Date = agoraEmSaoPaulo()
): JanelaCaixaAtiva {
  const ativa = encontrarJanelaCaixaAtiva(horarios, agora);
  if (ativa) return ativa;
  return janelaCaixaFallback(horarios, agora);
}

/** Caixa aberto cuja janela planejada já expirou (agora > data_fechamento do registro). */
export function caixaAbertoEstaVencido(caixa: CaixaLike, agora: Date = agoraEmSaoPaulo()): boolean {
  if (caixa.status !== "aberto") return false;
  const fim = new Date(caixa.data_fechamento).getTime();
  if (!Number.isFinite(fim)) return false;
  return agora.getTime() > fim;
}

export function payloadFecharCaixa(caixa: CaixaLike, agora: Date = agoraEmSaoPaulo()): Record<string, unknown> {
  return {
    status: "fechado",
    valor_fechamento: Number(caixa.valor_fechamento ?? 0),
    data_fechamento: formatarISOlocal(agora),
  };
}

export function payloadNovoCaixaAberto(
  janela: JanelaCaixaAtiva,
  clienteId: string
): Record<string, unknown> {
  return {
    status: "aberto",
    cliente_id: clienteId,
    data_abertura: janela.data_abertura_iso,
    data_fechamento: janela.data_fechamento_iso,
    valor_abertura: 0,
    valor_fechamento: 0,
    controle_dia: 0,
  };
}

/** YYYY-MM-DD da data civil em America/Sao_Paulo para um instante. */
export function formatYmdInSaoPaulo(d: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

function getSpYmdAndHour(instant: Date): { ymd: string; hour: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    hour12: false,
  }).formatToParts(instant);
  const map: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== "literal") map[p.type] = p.value;
  }
  const ymd = `${map.year}-${map.month}-${map.day}`;
  const hour = parseInt(map.hour, 10);
  return { ymd, hour };
}

function ymdAddDays(ymd: string, delta: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const u = Date.UTC(y, m - 1, d);
  const t = u + delta * 86400000;
  const x = new Date(t);
  return `${x.getUTCFullYear()}-${String(x.getUTCMonth() + 1).padStart(2, "0")}-${String(
    x.getUTCDate()
  ).padStart(2, "0")}`;
}

/**
 * Sem horário cadastrado: mesma regra prática do caixaVigente (0h–2h59 SP → dia anterior),
 * sempre usando relógio de São Paulo (evita UTC do `toISOString()`).
 */
export function getDataCaixaParaFiltroFallbackSp(instant: Date = agoraEmSaoPaulo()): string {
  let { ymd, hour } = getSpYmdAndHour(instant);
  if (hour < 3) {
    ymd = ymdAddDays(ymd, -1);
  }
  return ymd;
}

/**
 * Data YYYY-MM-DD que o get-orders deve usar: “dia de referência” do caixa vigente.
 * Com horários: início civil do turno (ex.: 00:20 após meia-noite ainda no turno que abriu ontem às 19h → data de ontem).
 * Sem horários: fallback 1h + margem 2h em SP.
 */
export function getDataReferenciaCaixaParaFiltro(horarioRaw?: unknown): string {
  const agoraSp = agoraEmSaoPaulo();
  const horarios = parseHorarioFuncionamentoRaw(horarioRaw ?? []);
  if (horarios.length > 0) {
    const j = getJanelaParaNovoCaixa(horarios, agoraSp);
    return formatYmdInSaoPaulo(j.abertura);
  }
  return getDataCaixaParaFiltroFallbackSp(agoraSp);
}

/** Mesma lógica de referência, para um instante arbitrário (ex.: horário em que o cliente montou o pedido). */
export function getDataReferenciaCaixaParaInstante(
  horarioRaw: unknown,
  instant: Date
): string {
  const horarios = parseHorarioFuncionamentoRaw(horarioRaw ?? []);
  const agoraSp = new Date(
    instant.toLocaleString("en-US", { timeZone: "America/Sao_Paulo" })
  );
  if (horarios.length > 0) {
    const j = getJanelaParaNovoCaixa(horarios, agoraSp);
    return formatYmdInSaoPaulo(j.abertura);
  }
  return getDataCaixaParaFiltroFallbackSp(instant);
}

/** Pedido pertence ao dia operacional YYYY-MM-DD (regra alinhada ao get-orders / caixa SP). */
export function orderMatchesOperationalDay(
  createdAt: string | Date | null | undefined,
  referenceYmd: string,
  horarioRaw?: unknown
): boolean {
  if (!referenceYmd?.trim() || !createdAt) return false;
  const instant = createdAt instanceof Date ? createdAt : new Date(createdAt);
  if (Number.isNaN(instant.getTime())) return false;
  return getDataReferenciaCaixaParaInstante(horarioRaw, instant) === referenceYmd.trim();
}

/** Remove pedidos duplicados (mesmo id) ao agregar vários dias no gerente. */
export function dedupeOrdersById<T extends { id?: number | string | null }>(orders: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const order of orders) {
    const key = order.id != null ? String(order.id) : "";
    if (!key) {
      out.push(order);
      continue;
    }
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(order);
  }
  return out;
}

/** Mantém pedidos cujo created_at cai em algum dia YYYY-MM-DD da lista (inclusivo). */
export function filterOrdersForOperationalDays<
  T extends { id?: number | string | null; created_at?: string | null },
>(
  orders: T[],
  days: string[],
  horarioRaw?: unknown
): T[] {
  const set = new Set(days.map((d) => d.trim()).filter(Boolean));
  if (set.size === 0) return orders;
  return dedupeOrdersById(
    orders.filter((o) => {
      if (!o.created_at) return false;
      for (const day of set) {
        if (orderMatchesOperationalDay(o.created_at, day, horarioRaw)) return true;
      }
      return false;
    })
  );
}

const TOLERANCIA_ABERTURA_CAIXA_MS = 125 * 60 * 1000; // ~2h — alinha janela estendida ao registro

/** Indica se a linha de caixa pertence à mesma janela operacional calculada. */
export function caixaPertenceAJanela(
  c: CaixaLike & { data_abertura: string },
  j: JanelaCaixaAtiva
): boolean {
  const tC = new Date(c.data_abertura).getTime();
  const tJ = j.aberturaCaixa.getTime();
  if (!Number.isFinite(tC) || !Number.isFinite(tJ)) return false;
  return Math.abs(tC - tJ) <= TOLERANCIA_ABERTURA_CAIXA_MS;
}
